import { useEffect, useState } from 'react'
import { useAuthStore } from '@/store/authStore'
import { saveBlob, timetableExportApi, type ExportOptions, type ExportRequest } from '@/services/api/timetableExport'

const PERIODS = [
  { id: 'this_week', title: 'Diese Woche', text: 'Montag bis Freitag. Der Stichtag ist jetzt.' },
  { id: 'one_week', title: 'Eine Woche', text: 'Die Woche, in der der Tag liegt. Nicht zwingend die heutige.' },
  { id: 'range', title: 'Datumsbereich', text: 'Zwei Daten. Länger als ein Schuljahr wird abgelehnt.' },
  { id: 'semester', title: 'Laufendes Semester', text: 'Wie im Schulkalender. Fehlt das Semester, gilt das Schuljahr.' },
  { id: 'from_today', title: 'Ab heute', text: 'Die Vorgabe kommt vom Superadmin. Diese Zahl gilt nur für diese Datei.' },
] as const

const MARKS = [
  { id: 'substitution', title: 'Bestätigte Vertretung', text: 'Der Lehrer der Stunde bleibt. Die Vertretung steht nur in der Marke.' },
  { id: 'gap', title: 'Offene Lücke', text: 'Die Stunde ist frei, die Suche läuft noch.' },
  { id: 'cancelled', title: 'Ausfall', text: 'Aus heisst: die Stunde bleibt, die Ausfall-Marke fehlt.' },
] as const

const PARTS = [
  { id: 'changes', title: 'Änderungsliste', file: 'aenderungen.pdf', text: 'Nur Stunden mit Marke.' },
  { id: 'teachers', title: 'Lehrer-Raster', file: 'lehrer.pdf', text: 'Eine Seite pro Lehrer. Der Lehrer der Stunde bleibt.' },
  { id: 'classes', title: 'Klassen-Raster', file: 'klassen.pdf', text: 'Dieselben Stunden, nach Klasse.' },
  { id: 'combined', title: 'Sammeldatei', file: 'sammel.pdf', text: 'Die gewählten Teile in einer Datei. Nicht ein viertes Raster.' },
] as const

const FORMAT_LABEL: Record<string, string> = {
  pdf: 'PDF',
  untis_substitution: 'Untis / WebUntis',
  webuntis_excel: 'WebUntis-Excel',
  stida_excel: 'STiDA-Excel',
  lehreroffice_gpuntis: 'LehrerOffice',
  schulnetz: 'Schulnetz',
}

const LINK_MODE: Record<string, string> = {
  bearer: 'Wer den Link hat',
  login: 'Login und Export-Recht',
  addressed: 'Nur die adressierte Person',
}

const LINK_TTL: Record<string, string> = {
  '24h': '24 Stunden',
  '7d': '7 Tage',
  '30d': '30 Tage',
  once: 'Einmal laden',
}

const MARK_ON: Record<string, string> = {
  substitution: 'border-green-600 bg-green-100 text-green-800',
  gap: 'border-orange-600 bg-orange-100 text-orange-800',
  cancelled: 'border-red-600 bg-red-100 text-red-800',
}

const STEPS = ['Zeitraum', 'Inhalt', 'Ausgabe']
const DAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr']

function isoOf(date: Date): string {
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${m}-${d}`
}

function mondayOf(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(y || 2026, (m || 1) - 1, d || 1)
  const day = date.getDay() || 7
  date.setDate(date.getDate() - (day - 1))
  return date
}

function weekDays(iso: string): Array<{ label: string; n: number; iso: string }> {
  const start = mondayOf(iso)
  return DAY_LABELS.map((label, index) => {
    const date = new Date(start)
    date.setDate(start.getDate() + index)
    return { label, n: date.getDate(), iso: isoOf(date) }
  })
}

function weekSpan(iso: string): string {
  const days = weekDays(iso)
  const first = days[0]
  const last = days[4]
  if (!first || !last) return iso
  return `${first.n}.–${last.n}.`
}

async function readDetail(err: unknown): Promise<string> {
  const data = (err as { response?: { data?: unknown } })?.response?.data
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as { detail?: string }
      if (typeof parsed.detail === 'string') return parsed.detail
    } catch {
      return 'Export fehlgeschlagen.'
    }
  }
  const detail = (data as { detail?: string } | undefined)?.detail
  return typeof detail === 'string' ? detail : 'Export fehlgeschlagen.'
}

export function ExportRail({ options }: { options: ExportOptions }) {
  const role = useAuthStore(state => state.user?.acting_role || state.user?.role || '')
  const schoolMail = role !== '' && role !== 'teacher'
  const [step, setStep] = useState(0)
  const [period, setPeriod] = useState<string>('this_week')
  const [weekStart, setWeekStart] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [days, setDays] = useState(options.horizon_days || 14)
  const [marks, setMarks] = useState<string[]>(['substitution', 'gap', 'cancelled'])
  const [names, setNames] = useState('readable')
  const [parts, setParts] = useState<string[]>(['teachers', 'classes', 'changes'])
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const pref = options.preference
    if (!pref) {
      setDays(options.horizon_days || 14)
      return
    }
    setPeriod(pref.period_kind || 'this_week')
    setDays(pref.period_n || options.horizon_days || 14)
    setMarks(pref.change_types?.length ? pref.change_types : ['substitution', 'gap', 'cancelled'])
    setNames(pref.name_mode || 'readable')
    setParts(pref.pdf_parts?.length ? pref.pdf_parts : ['teachers', 'classes', 'changes'])
    setWeekStart(pref.week_start || '')
    setDateFrom(pref.date_from || '')
    setDateTo(pref.date_to || '')
  }, [options])

  const toggle = (list: string[], value: string, setList: (next: string[]) => void) => {
    setList(list.includes(value) ? list.filter(item => item !== value) : [...list, value])
  }

  const periodLine = () => {
    if (period === 'one_week') return weekStart ? `Eine Woche ab ${weekStart}` : 'Eine Woche, Datum fehlt'
    if (period === 'range') return dateFrom && dateTo ? `${dateFrom} bis ${dateTo}` : 'Datumsbereich, Von oder Bis fehlt'
    if (period === 'from_today') return `Ab heute, ${days} Tage`
    if (period === 'semester') return 'Laufendes Semester'
    return 'Diese Woche'
  }

  const markLine = () => {
    const on = MARKS.filter(item => marks.includes(item.id)).map(item => item.title)
    return on.length ? on.join(', ') : 'Keine Marke'
  }

  const partFiles = PARTS.filter(item => parts.includes(item.id)).map(item => item.file)
  const windowError = () => {
    if (period === 'one_week' && !weekStart) return 'Für Eine Woche ein Datum wählen.'
    if (period === 'range' && (!dateFrom || !dateTo)) return 'Für den Bereich Von und Bis wählen.'
    if (period === 'range' && dateFrom > dateTo) return 'Von liegt nach Bis.'
    return null
  }
  const partsError = parts.length === 0 ? 'Mindestens eine Datei. Sonst gibt es keine ZIP.' : null

  const payload = (): ExportRequest => ({
    period_kind: period,
    period_n: days,
    change_types: marks,
    name_mode: names,
    pdf_parts: parts,
    week_start: weekStart || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    formats: ['pdf'],
  })

  const run = async (kind: 'download' | 'mail') => {
    const blocked = windowError() || partsError
    if (blocked) {
      setError(blocked)
      return
    }
    setBusy(true)
    setError(null)
    setInfo(null)
    try {
      if (kind === 'download') {
        const response = await timetableExportApi.download(payload())
        saveBlob(response.data, 'stundenplan.zip')
      } else {
        await timetableExportApi.mail({ ...payload(), school_mail: true })
        setInfo('Der Link ist in der Mail. Die Montag-Mail bleibt unverändert.')
      }
    } catch (err: unknown) {
      setError(await readDetail(err))
    } finally {
      setBusy(false)
    }
  }

  const addresses = options.mail_addresses || []
  const linkNote = `${LINK_MODE[options.link_mode] || options.link_mode}, ${LINK_TTL[options.link_ttl] || options.link_ttl}`

  return (
    <div className="grid min-h-[640px] gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
      <div className="flex flex-col gap-2">
        {STEPS.map((name, index) => {
          const detail = index === 0 ? periodLine() : index === 1 ? markLine() : 'Download oder Mail'
          const on = step === index
          return (
            <button
              key={name}
              type="button"
              className={`rounded-lg border px-3 py-2 text-left ${on ? 'border-blue-600 bg-blue-50 text-blue-900' : 'border-gray-200 bg-white text-gray-800'}`}
              onClick={() => setStep(index)}
            >
              <span className={`block text-[10px] font-bold uppercase tracking-wider ${on ? 'text-blue-700' : 'text-gray-400'}`}>Schritt {index + 1}</span>
              <span className="block text-sm font-semibold">{name}</span>
              <span className={`block text-xs ${on ? 'text-blue-700' : 'text-gray-500'}`}>{detail}</span>
            </button>
          )
        })}
        <p className="mt-auto text-xs text-gray-500">Die Schiene merkt die Wahl. Ein früherer Schritt löscht sie nicht. Wer den Punkt sieht, stellt der Superadmin ein.</p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-5">
        {step === 0 && (
          <div className="space-y-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Schritt 1</p>
            <h2 className="text-2xl font-medium">Welches Fenster?</h2>
            <p className="text-sm text-gray-600">Der wirksame Plan, nicht der Standardplan. Der Zeitraum schneidet. Er entscheidet nicht, wer exportieren darf.</p>
            <div className="grid gap-2">
              {PERIODS.map(item => (
                <button
                  key={item.id}
                  type="button"
                  className={`rounded-lg border px-3 py-2 text-left ${period === item.id ? 'border-blue-600 bg-blue-50 shadow-[inset_3px_0_0_#2563eb]' : 'border-gray-200'}`}
                  onClick={() => setPeriod(item.id)}
                >
                  <b className="block text-sm">{item.title}</b>
                  <span className="block text-xs text-gray-500">{item.text}</span>
                </button>
              ))}
            </div>
            {period === 'one_week' && (
              <input className="rounded border border-gray-200 px-2 py-1 text-sm" type="date" value={weekStart} aria-label="Tag in der Woche" onChange={event => setWeekStart(event.target.value)} />
            )}
            {period === 'range' && (
              <div className="flex gap-2">
                <input className="rounded border border-gray-200 px-2 py-1 text-sm" type="date" value={dateFrom} aria-label="Von" onChange={event => setDateFrom(event.target.value)} />
                <input className="rounded border border-gray-200 px-2 py-1 text-sm" type="date" value={dateTo} aria-label="Bis" onChange={event => setDateTo(event.target.value)} />
              </div>
            )}
            {period === 'from_today' && (
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input className="w-20 rounded border border-gray-200 px-2 py-1" type="number" min={1} value={days} aria-label="Tage" onChange={event => setDays(Math.max(1, Number(event.target.value) || 1))} />
                Tage, nur diese Datei. Die Montag-Mail hat ihre eigene Zahl.
              </label>
            )}
            {(period === 'this_week' || (period === 'one_week' && weekStart)) && (
              <div>
                <div className="grid grid-cols-5 gap-1">
                  {weekDays(period === 'one_week' ? weekStart : isoOf(new Date())).map(day => (
                    <div key={day.iso} className="rounded border border-gray-200 bg-stone-50 px-2 py-1 text-center text-xs">
                      <strong className="block">{day.label} {day.n}</strong>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-sm text-gray-600">{weekSpan(period === 'one_week' ? weekStart : isoOf(new Date()))}. Ob die Marke gedruckt wird, entscheidet der nächste Schritt.</p>
              </div>
            )}
            {windowError() && <p className="text-sm text-amber-800">{windowError()}</p>}
            <div className="flex justify-end">
              <button type="button" className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white disabled:opacity-40" disabled={Boolean(windowError())} onClick={() => setStep(1)}>Weiter, was gedruckt wird</button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Schritt 2 · {periodLine()}</p>
            {windowError() && <p className="text-sm text-amber-800">{windowError()}</p>}
            <h2 className="text-2xl font-medium">Was wird gedruckt?</h2>
            <p className="text-sm text-gray-600">Abwählen nimmt die Marke weg, nicht die Stunde. Der Grund der Abwesenheit steht nie in der Datei.</p>
            <div>
              <h3 className="mb-2 text-sm font-semibold">Marken</h3>
              {MARKS.map(item => {
                const on = marks.includes(item.id)
                return (
                  <label key={item.id} className={`mb-2 flex gap-2 rounded-lg border px-3 py-2 text-sm ${on ? MARK_ON[item.id] : 'border-gray-200 bg-white text-gray-800'}`}>
                    <input type="checkbox" checked={on} onChange={() => toggle(marks, item.id, setMarks)} />
                    <span><b>{item.title}</b><small className={`block ${on ? 'opacity-80' : 'text-gray-500'}`}>{item.text}</small></span>
                  </label>
                )
              })}
            </div>
            <div>
              <h3 className="mb-2 text-sm font-semibold">Namen</h3>
              <div className="grid gap-2 sm:grid-cols-2">
                <button type="button" className={`rounded-lg border px-3 py-2 text-left ${names === 'readable' ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}`} onClick={() => setNames('readable')}>
                  <b className="block text-sm">Lesbare Namen</b><span className="text-xs text-gray-500">Meier, nicht das Import-Kürzel.</span>
                </button>
                <button type="button" className={`rounded-lg border px-3 py-2 text-left ${names === 'code' ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}`} onClick={() => setNames('code')}>
                  <b className="block text-sm">Kürzel des Imports</b><span className="text-xs text-gray-500">Kürzel des Imports, nicht der Name nach dem Mapping.</span>
                </button>
              </div>
            </div>
            <div>
              <h3 className="mb-1 text-sm font-semibold">Dateien</h3>
              <p className="mb-2 text-xs text-gray-500">Die Liste zuerst, dann die Raster. Die Sammeldatei ist keine zusätzliche Sicht.</p>
              {PARTS.map(item => (
                <label key={item.id} className="mb-2 flex gap-2 text-sm">
                  <input type="checkbox" checked={parts.includes(item.id)} onChange={() => toggle(parts, item.id, setParts)} />
                  <span><b>{item.title}</b> · {item.file}<small className="block text-gray-500">{item.text}</small></span>
                </label>
              ))}
              {partsError && <p className="text-sm text-amber-800">{partsError}</p>}
            </div>
            <div className="flex justify-between">
              <button type="button" className="rounded border px-3 py-1.5 text-sm" onClick={() => setStep(0)}>Zurück</button>
              <button type="button" className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white disabled:opacity-40" disabled={Boolean(windowError() || partsError)} onClick={() => setStep(2)}>Weiter zur Ausgabe</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Schritt 3</p>
            <h2 className="text-2xl font-medium">Laden oder schicken.</h2>
            <p className="text-sm text-gray-600">{periodLine()}. {markLine()}. {names === 'code' ? 'Kürzel' : 'Lesbare Namen'}.</p>
            {(windowError() || partsError) && <p className="text-sm text-amber-800">{windowError() || partsError}</p>}
            <p className="text-xs text-gray-500">PDF-Teile, schon gewählt: {partFiles.join(', ') || '—'}. Das ist der Inhalt der ZIP, kein zweites Format.</p>
            <div>
              <h3 className="mb-1 text-sm font-semibold">Formate</h3>
              <p className="mb-2 text-xs text-gray-500">PDF folgt den Teilen. Die anderen bleiben sichtbar und gesperrt. Ankreuzen schreibt sie nicht.</p>
              {(options.formats || []).map(item => (
                <label key={item.id} className="mb-2 flex gap-2 text-sm text-gray-500">
                  <input type="checkbox" disabled checked={item.id === 'pdf'} />
                  <span>
                    <b className="text-gray-800">{FORMAT_LABEL[item.id] || item.id}</b>
                    <small className="block">{item.capable ? 'Folgt den PDF-Teilen. Kein zweites Kreuz.' : `gesperrt · ${item.reason}`}</small>
                  </span>
                </label>
              ))}
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <button type="button" className="rounded-lg bg-blue-600 px-4 py-3 text-left text-white disabled:opacity-40" disabled={busy || Boolean(windowError() || partsError)} onClick={() => run('download')}>
                <b className="block">Download</b>
                <span className="block text-xs text-blue-100">stundenplan.zip. Darin {partFiles.join(', ') || '—'}. Bytes, kein Link. Keine Programmdatei.</span>
              </button>
              {schoolMail ? (
                <button type="button" className="rounded-lg border border-gray-300 px-4 py-3 text-left disabled:opacity-40" disabled={busy || addresses.length === 0 || Boolean(windowError() || partsError)} onClick={() => run('mail')}>
                  <b className="block">Link an die Schul-Adressen</b>
                  <span className="block text-xs text-gray-500">Ein Link, kein Anhang. Dieselbe ZIP. Die Montag-Mail bleibt, wie der Superadmin sie gestellt hat.</span>
                  <span className="mt-2 block">
                    {addresses.length === 0
                      ? <span className="text-xs text-amber-800">Keine Adresse, der Zeitplan läuft nicht.</span>
                      : addresses.map(address => (
                        <span key={address} className="mr-1 mt-1 inline-block rounded-full border border-gray-300 px-2 py-0.5 text-xs">{address}</span>
                      ))}
                  </span>
                </button>
              ) : null}
            </div>
            <pre className="whitespace-pre-wrap rounded-lg bg-stone-50 p-3 font-mono text-xs text-gray-700">{`hinweis.txt
${periodLine()}
Marken: ${markLine()}
Namen: ${names === 'code' ? 'Kürzel' : 'lesbar'}
PDF-Teile: ${partFiles.join(', ') || '—'}
Formate aus: ${(options.formats || []).filter(item => !item.capable).map(item => `${FORMAT_LABEL[item.id] || item.id}: ${item.reason}`).join('; ') || '—'}
kein Abwesenheitsgrund`}</pre>
            <div className="flex flex-wrap gap-2 text-xs text-gray-500">
              <span className="rounded-full bg-stone-50 px-2 py-1">Montag-Mail unverändert</span>
              <span className="rounded-full bg-stone-50 px-2 py-1">kein Abwesenheitsgrund</span>
              {options.link_mode ? <span className="rounded-full bg-stone-50 px-2 py-1">Link-Schutz nur lesen · {linkNote}</span> : null}
              <span className="rounded-full bg-stone-50 px-2 py-1">Rechte beim Superadmin</span>
            </div>
            {error && <p className="text-sm text-red-700">{error}</p>}
            {info && <p className="text-sm text-green-700">{info}</p>}
            <button type="button" className="rounded border px-3 py-1.5 text-sm" onClick={() => setStep(1)}>Zurück zum Inhalt</button>
          </div>
        )}
      </div>
    </div>
  )
}
